import { ExportButton } from "@/components/ExportButton";
import {
  DatagridConfigurable as Datagrid,
  FilterButton,
  FunctionField,
  List,
  NumberField,
  Pagination,
  SelectColumnsButton,
  SelectInput,
  ShowButton,
  SimpleList,
  TextField,
  TextInput,
  TopToolbar,
  usePermissions,
  useStore,
  type Exporter,
  type RaRecord,
} from "react-admin";
import { Box, Link as MuiLink, Typography, useMediaQuery } from "@mui/material";
import { isAsharaEvent } from "@/utils";
import {
  formatVenueLabel,
  getAsharaVenueChoices,
  getJamanVenueChoices,
} from "@/utils/event-venue-utils";
import { hasPermission } from "@/utils/permission-utils";
import { exportToExcel } from "@/utils/export-to-excel";
import type { CurrentEvent } from "@/containers/events/types";

const PIRSA_LIST_FILTER = { pirsa_gt: 0 };

const formatPirsaFulfillment = (record: RaRecord) =>
  record.pirsaSelfPickup ? "Self pickup" : "Delivery needed";

export const PirsaDeliveryList = () => {
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"), { noSsr: true });
  const [currentEvent] = useStore<CurrentEvent | null>("currentEvent", null);
  const isAshara = isAsharaEvent(currentEvent ?? undefined);
  const { permissions } = usePermissions();

  const filters = [
    <TextInput
      label="Search by HOF name, ITS, form no, phone, address"
      source="search"
      alwaysOn
      key="search"
      sx={{ minWidth: { xs: "100%", md: 360 } }}
    />,
    isAshara ? (
      <SelectInput
        label="Venue"
        source="markaz"
        key="markaz"
        choices={getAsharaVenueChoices(currentEvent)}
        alwaysOn
      />
    ) : (
      <SelectInput
        label="Jaman venue"
        source="markaz"
        key="markaz"
        choices={getJamanVenueChoices(currentEvent)}
        alwaysOn
      />
    ),
    <SelectInput
      label="Fulfillment"
      source="pirsaSelfPickup"
      key="pirsaSelfPickup"
      emptyText="All fulfillments"
      choices={[
        { id: false, name: "Delivery needed" },
        { id: true, name: "Self pickup" },
      ]}
    />,
  ].flat();

  const ListActions = () => (
    <TopToolbar sx={{ justifyContent: "start", flexWrap: "wrap", gap: 1 }}>
      <FilterButton />
      <SelectColumnsButton />
      {hasPermission(permissions, "niyaaz.export") && <ExportButton />}
    </TopToolbar>
  );

  const exporter: Exporter = (records) => {
    const columns = [
      { header: "Form No", field: "formNo", width: 12 },
      ...(isAshara
        ? [{ header: "Venue", field: "markaz", width: 18 }]
        : [{ header: "Markaz", field: "markaz", width: 15 }]),
      { header: "HOF ITS", field: "HOFId", width: 14 },
      { header: "HOF Name", field: "HOFName", width: 28 },
      { header: "Mobile", field: "HOFPhone", width: 16 },
      { header: "Address", field: "HOFAddress", width: 40 },
      { header: "Pirsa packets", field: "pirsa", width: 12 },
      {
        header: "Fulfillment",
        field: (rec: RaRecord) => formatPirsaFulfillment(rec),
        width: 16,
      },
      {
        header: "Venue label",
        field: (rec: RaRecord) => formatVenueLabel(rec.markaz, currentEvent),
        width: 20,
      },
    ];

    exportToExcel(columns, records, {
      filenamePrefix: `${currentEvent?.slug ?? "event"}-pirsa-delivery`,
      sheetName: "Pirsa delivery",
    });
  };

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 2, pb: 0 }}>
        Registrations with pirsa for {currentEvent?.name ?? "this event"}. Use address and phone
        columns for delivery.
      </Typography>
      <List
        resource="niyaazPirsa"
        pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100, 250]} />}
        filters={filters}
        actions={<ListActions />}
        filter={PIRSA_LIST_FILTER}
        sort={{ field: "markaz", order: "ASC" }}
        perPage={50}
        exporter={hasPermission(permissions, "niyaaz.export") ? exporter : undefined}
        title="Pirsa delivery"
      >
        {isSmall ? (
          <SimpleList
            primaryText={(record) => String(record.HOFName ?? record.formNo ?? "")}
            secondaryText={(record) => (
              <>
                {String(record.HOFAddress ?? "No address")}
                <br />
                {String(record.HOFPhone ?? "No phone")} · {Number(record.pirsa ?? 0)} packet(s)
                <br />
                {formatPirsaFulfillment(record)} · {formatVenueLabel(record.markaz, currentEvent)} ·
                Form {String(record.formNo)}
              </>
            )}
            tertiaryText={(record) => `ITS ${String(record.HOFId ?? "")}`}
            linkType={false}
            rowClick={(id) => `/niyaaz/${id}/show`}
            rowSx={() => ({ borderBottom: 1, borderBottomColor: "divider" })}
          />
        ) : (
          <Box sx={{ overflowX: "auto", width: "100%" }}>
            <Datagrid
              rowClick={(id) => `/niyaaz/${id}/show`}
              bulkActionButtons={false}
              sx={{ minWidth: 1100 }}
            >
              <ShowButton resource="niyaaz" label="" />
              <TextField source="formNo" label="Form No" />
              <FunctionField
                label={isAshara ? "Venue" : "Markaz"}
                source="markaz"
                render={(record: RaRecord) => formatVenueLabel(record?.markaz, currentEvent)}
              />
              <TextField source="HOFId" label="HOF ITS" />
              <TextField source="HOFName" label="HOF Name" />
              <FunctionField
                label="Mobile"
                source="HOFPhone"
                render={(record: RaRecord) => {
                  const phone = record?.HOFPhone;
                  if (!phone) return "—";
                  const tel = String(phone).replace(/\s/g, "");
                  return (
                    <MuiLink href={`tel:${tel}`} onClick={(e) => e.stopPropagation()}>
                      {String(phone)}
                    </MuiLink>
                  );
                }}
              />
              <TextField source="HOFAddress" label="Address" />
              <FunctionField
                label="Fulfillment"
                source="pirsaSelfPickup"
                render={(record: RaRecord) => formatPirsaFulfillment(record)}
              />
              <NumberField source="pirsa" label="Packets" textAlign="left" />
            </Datagrid>
          </Box>
        )}
      </List>
    </>
  );
};
